"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { SidebarSection } from "@/components/navigation/navTypes";
interface WebSidebarSubmenuProps {
  readonly section: SidebarSection;
  readonly pathname: string | null;
  readonly onClose: () => void;
  readonly leftOffset?: number | undefined;
  readonly anchorTop?: number | undefined;
  readonly anchorHeight?: number | undefined;
  readonly triggerElement?: HTMLElement | null | undefined;
}
function WebSidebarSubmenu(t0) {
  const $ = _c(40);
  const {
    section,
    pathname,
    onClose,
    leftOffset,
    anchorTop,
    anchorHeight,
    triggerElement
  } = t0;
  const containerRef = useRef(null);
  const hasGlobalContext = typeof globalThis !== "undefined";
  const browserWindow = hasGlobalContext ? globalThis.window as Window | undefined : undefined;
  const browserDocument = hasGlobalContext ? globalThis.document as Document | undefined : undefined;
  let t1;
  if ($[0] !== anchorTop) {
    t1 = () => anchorTop ?? 16;
    $[0] = anchorTop;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [computedTop, setComputedTop] = useState(t1);
  let t2;
  if ($[2] !== section.items || $[3] !== section.subsections) {
    t2 = [...section.items, ...(section.subsections?.flatMap(_temp) ?? [])];
    $[2] = section.items;
    $[3] = section.subsections;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const combinedItems = t2;
  let t3;
  if ($[5] !== onClose) {
    t3 = event => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    $[5] = onClose;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleKeyDown = t3;
  let t4;
  if ($[7] !== onClose || $[8] !== triggerElement) {
    t4 = event_0 => {
      const target = event_0.target;
      if (!(target instanceof Node)) {
        return;
      }
      const container = containerRef.current;
      if (container?.contains(target)) {
        return;
      }
      if (triggerElement?.contains(target)) {
        return;
      }
      onClose();
    };
    $[7] = onClose;
    $[8] = triggerElement;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleClickOutside = t4;
  let t5;
  let t6;
  if ($[10] !== handleClickOutside || $[11] !== handleKeyDown) {
    t5 = () => {
      if (browserWindow === undefined || browserDocument === undefined) {
        return;
      }
      browserWindow.addEventListener("keydown", handleKeyDown);
      browserDocument.addEventListener("mousedown", handleClickOutside);
      browserDocument.addEventListener("touchstart", handleClickOutside);
      return () => {
        browserWindow.removeEventListener("keydown", handleKeyDown);
        browserDocument.removeEventListener("mousedown", handleClickOutside);
        browserDocument.removeEventListener("touchstart", handleClickOutside);
      };
    };
    t6 = [browserWindow, browserDocument, handleKeyDown, handleClickOutside];
    $[10] = handleClickOutside;
    $[11] = handleKeyDown;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useEffect(t5, t6);
  let t7;
  if ($[14] !== anchorHeight || $[15] !== anchorTop) {
    t7 = () => {
      if (browserWindow && containerRef.current) {
        const menuRect = containerRef.current.getBoundingClientRect();
        const viewportHeight = browserWindow.innerHeight || 0;
        const anchorCenter = anchorTop !== undefined && anchorHeight !== undefined ? anchorTop + anchorHeight / 2 : (anchorTop ?? 16) + menuRect.height / 2;
        let top = anchorCenter - menuRect.height / 2;
        top = Math.max(16, Math.min(top, viewportHeight - menuRect.height - 16));
        setComputedTop(previous => previous === top ? previous : top);
      }
    };
    $[14] = anchorHeight;
    $[15] = anchorTop;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== anchorHeight || $[18] !== anchorTop || $[19] !== combinedItems.length || $[20] !== section.key) {
    t8 = [browserWindow, anchorTop, anchorHeight, section.key, combinedItems.length];
    $[17] = anchorHeight;
    $[18] = anchorTop;
    $[19] = combinedItems.length;
    $[20] = section.key;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  useLayoutEffect(t7, t8);
  let t9;
  if ($[22] !== pathname) {
    t9 = href => pathname === href;
    $[22] = pathname;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const isActive = t9;
  if (browserDocument === undefined) {
    return null;
  }
  const portalTarget = browserDocument.body;
  if (!portalTarget) {
    return null;
  }
  const leftStyle = leftOffset === undefined ? "calc(var(--layout-margin, 0px) + 80px)" : `${leftOffset}px`;
  let t10;
  if ($[24] !== computedTop) {
    t10 = Number.isFinite(computedTop) ? `${computedTop}px` : "16px";
    $[24] = computedTop;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const topStyle = t10;
  let t11;
  if ($[26] !== leftStyle || $[27] !== topStyle) {
    t11 = {
      left: leftStyle,
      top: topStyle
    };
    $[26] = leftStyle;
    $[27] = topStyle;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const t12 = `${section.name} sub-navigation`;
  let t13;
  if ($[29] !== section.name) {
    t13 = <div className="tw-px-6 tw-pt-4 tw-pb-2 tw-border-b tw-border-iron-700 tw-border-solid tw-border-x-0 tw-border-t-0"><h3 className="tw-text-base tw-font-semibold tw-text-iron-50">{section.name}</h3></div>;
    $[29] = section.name;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== combinedItems || $[32] !== isActive || $[33] !== onClose) {
    t14 = <div className="tw-flex-1 tw-p-3 tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300">{combinedItems.map(item => {
        const active = isActive(item.href);
        return <Link key={item.href} href={item.href} className={`tw-group tw-flex tw-items-center tw-no-underline tw-rounded-lg tw-px-3 tw-py-2 tw-mb-1 tw-text-md tw-transition-all tw-duration-300 tw-touch-action-manipulation focus:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-iron-600 focus-visible:tw-ring-offset-1 tw-ring-offset-iron-950 ${active ? "tw-text-white tw-bg-iron-700 tw-font-semibold desktop-hover:hover:tw-text-white" : "tw-text-iron-300 desktop-hover:hover:tw-bg-iron-700/50 desktop-hover:hover:tw-text-iron-50 tw-font-medium"}`} aria-current={active ? "page" : undefined} role="menuitem" onClick={onClose}><span className="tw-truncate">{item.name}</span></Link>;
      })}</div>;
    $[31] = combinedItems;
    $[32] = isActive;
    $[33] = onClose;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t11 || $[36] !== t12 || $[37] !== t13 || $[38] !== t14) {
    t15 = createPortal(<div ref={containerRef} className="tailwind-scope tw-fixed tw-z-[95] tw-w-64 tw-max-h-[65vh] tw-bg-iron-800 tw-border tw-border-solid tw-border-iron-700 tw-rounded-lg tw-shadow-[0_20px_45px_rgba(7,7,11,0.7)] tw-overflow-hidden tw-flex tw-flex-col" style={t11} role="menu" aria-label={t12}>{t13}{t14}</div>, portalTarget);
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  return t15;
}
function _temp(sub) {
  return sub.items;
}
export default WebSidebarSubmenu;